/**
 * Native Web MIDI API listener.
 * Routes note-on / note-off / velocity into application callbacks
 * for immediate synth playback on device speakers.
 */

export interface MidiNoteEvent {
  type: "noteon" | "noteoff";
  midiNote: number;
  velocity: number;
  channel: number;
  timestamp: number;
  sourceId: string;
  sourceName: string;
}

export interface MidiDeviceInfo {
  id: string;
  name: string;
  manufacturer: string;
  state: MIDIPortDeviceState;
  connection: MIDIPortConnectionState;
}

export interface MidiCcEvent {
  controller: number;
  value: number;
  channel: number;
  timestamp: number;
  sourceId: string;
  sourceName: string;
}

export type MidiNoteHandler = (event: MidiNoteEvent) => void;
export type MidiDeviceHandler = (devices: MidiDeviceInfo[]) => void;
export type MidiCcHandler = (event: MidiCcEvent) => void;

export interface MidiInterfaceStatus {
  supported: boolean;
  accessGranted: boolean;
  inputCount: number;
  error: string | null;
}

function isMidiSupported(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.requestMIDIAccess === "function";
}

export class MidiInterface {
  private access: MIDIAccess | null = null;
  private noteHandlers = new Set<MidiNoteHandler>();
  private deviceHandlers = new Set<MidiDeviceHandler>();
  private ccHandlers = new Set<MidiCcHandler>();
  private boundPorts = new Map<string, (e: MIDIMessageEvent) => void>();
  private status: MidiInterfaceStatus = {
    supported: isMidiSupported(),
    accessGranted: false,
    inputCount: 0,
    error: null,
  };

  getStatus(): MidiInterfaceStatus {
    return { ...this.status };
  }

  onNote(handler: MidiNoteHandler): () => void {
    this.noteHandlers.add(handler);
    return () => this.noteHandlers.delete(handler);
  }

  onControlChange(handler: MidiCcHandler): () => void {
    this.ccHandlers.add(handler);
    return () => this.ccHandlers.delete(handler);
  }

  onDevicesChanged(handler: MidiDeviceHandler): () => void {
    this.deviceHandlers.add(handler);
    return () => this.deviceHandlers.delete(handler);
  }

  /**
   * Request MIDI access (sysex off for privacy / simplicity).
   * Safe to call multiple times — reuses existing access.
   */
  async connect(options: { sysex?: boolean } = {}): Promise<MidiInterfaceStatus> {
    if (!isMidiSupported()) {
      this.status = {
        supported: false,
        accessGranted: false,
        inputCount: 0,
        error: "Web MIDI API is not supported in this browser/webview.",
      };
      return this.getStatus();
    }

    try {
      this.access = await navigator.requestMIDIAccess({ sysex: options.sysex ?? false });
      this.status.accessGranted = true;
      this.status.error = null;

      this.access.onstatechange = () => this.refreshInputs();
      this.refreshInputs();
    } catch (err) {
      this.status.accessGranted = false;
      this.status.error =
        err instanceof Error ? err.message : "MIDI permission denied or unavailable.";
    }

    return this.getStatus();
  }

  listInputs(): MidiDeviceInfo[] {
    if (!this.access) return [];
    const list: MidiDeviceInfo[] = [];
    this.access.inputs.forEach((input) => {
      list.push({
        id: input.id,
        name: input.name ?? "Unknown MIDI Input",
        manufacturer: input.manufacturer ?? "",
        state: input.state,
        connection: input.connection,
      });
    });
    return list;
  }

  private refreshInputs(): void {
    if (!this.access) return;

    // Detach listeners from ports that disappeared
    for (const [id, listener] of this.boundPorts) {
      const stillThere = this.access.inputs.get(id);
      if (!stillThere) {
        this.boundPorts.delete(id);
      } else {
        stillThere.onmidimessage = listener;
      }
    }

    this.access.inputs.forEach((input) => {
      if (this.boundPorts.has(input.id)) return;
      const listener = (e: MIDIMessageEvent) => this.handleMessage(e, input);
      this.boundPorts.set(input.id, listener);
      input.onmidimessage = listener;
    });

    this.status.inputCount = this.access.inputs.size;
    const devices = this.listInputs();
    for (const h of this.deviceHandlers) h(devices);
  }

  private handleMessage(e: MIDIMessageEvent, input: MIDIInput): void {
    const data = e.data;
    if (!data || data.length < 2) return;

    const status = data[0];
    const command = status & 0xf0;
    const channel = status & 0x0f;
    const note = data[1];
    const velocity = data.length > 2 ? data[2] : 0;
    const ts = e.timeStamp;

    let type: "noteon" | "noteoff" | null = null;

    if (command === 0x90) {
      type = velocity > 0 ? "noteon" : "noteoff";
    } else if (command === 0x80) {
      type = "noteoff";
    } else if (command === 0xb0) {
      // Control change — sustain (CC64) and All Notes Off (CC123)
      if (note === 123) {
        for (let n = 0; n < 128; n++) {
          this.emit({
            type: "noteoff",
            midiNote: n,
            velocity: 0,
            channel,
            timestamp: ts,
            sourceId: input.id,
            sourceName: input.name ?? "MIDI",
          });
        }
        return;
      }
      if (note === 64) {
        for (const h of this.ccHandlers) {
          h({
            controller: 64,
            value: velocity,
            channel,
            timestamp: ts,
            sourceId: input.id,
            sourceName: input.name ?? "MIDI",
          });
        }
        return;
      }
      return;
    }

    if (!type) return;

    this.emit({
      type,
      midiNote: note,
      velocity,
      channel,
      timestamp: ts,
      sourceId: input.id,
      sourceName: input.name ?? "MIDI",
    });
  }

  private emit(event: MidiNoteEvent): void {
    for (const h of this.noteHandlers) h(event);
  }

  disconnect(): void {
    if (this.access) {
      this.access.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
      this.access.onstatechange = null;
    }
    this.boundPorts.clear();
    this.access = null;
    this.status.accessGranted = false;
    this.status.inputCount = 0;
  }
}
